// ---------------------------------------------------------------------------
// YouAreYou (uru) — Notification Context Types & Context Object
//
// Context definition and types only — no React components.
// Provider lives in NotificationContext.tsx.
// Hook lives in hooks/useNotifications.ts.
// ---------------------------------------------------------------------------

import { createContext } from "react";
import type { AppNotification } from "@/types/api";

// ---------------------------------------------------------------------------
// Toast — ephemeral WS-delivered alerts shown briefly at the top of the screen
// ---------------------------------------------------------------------------

export interface ToastItem {
  id: string;
  message: string;
  kind: "success" | "info" | "warning";
  /** Optional link to navigate to on click. */
  actionUrl?: string;
}

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export interface NotificationContextValue {
  /** Unread notifications from REST inbox. */
  notifications: AppNotification[];
  /** Number of unread notifications. */
  unreadCount: number;
  /** Whether the initial REST fetch is in progress. */
  isLoading: boolean;
  /** Ephemeral toast items currently visible. */
  toasts: ToastItem[];
  /** Mark a notification as read via the REST API. */
  markRead: (id: number) => Promise<void>;
  /** Add an ephemeral toast (auto-dismisses after 4s). */
  addToast: (toast: Omit<ToastItem, "id">) => void;
  /** Dismiss a toast by id. */
  dismissToast: (id: string) => void;
  /** Re-fetch notifications from the REST API. */
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const NotificationContext =
  createContext<NotificationContextValue | null>(null);
